import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { CannotRenewReason } from '@/models/CarePlanModel';
import { Queries } from '@/queries/Queries';
import { Button } from '@/uiKit/atoms/Button.tsx';
import { PatientRenewal } from '@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewal.tsx';

type RenewalButtonProps = {
  'data-source': 'monitoring' | 'settings' | 'header';
  patientId: string;
};

const cannotRenewTranslationMap: Record<CannotRenewReason, string> = {
  has_upcoming: 'diabetesForms.renewed',
  has_careplan_until_term: 'diabetesForms.hasUntilTerm',
};

export const RenewalButton = ({
  'data-source': dataSource,
  patientId,
}: RenewalButtonProps) => {
  const carePlans = Queries.practitioner.useCarePlans(patientId);
  const { t } = useTranslation();
  const [isTelemonitoringDrawerOpen, setIsTelemonitoringDrawerOpen] =
    useState(false);

  if (!carePlans.can_renew_care_plan) {
    const buttonLabel = carePlans.cannot_renew_reason
      ? t(cannotRenewTranslationMap[carePlans.cannot_renew_reason])
      : t('diabetesForms.renewed');

    return (
      <>
        <Button id="disabled-renew-tls-button" variant="outlined" disabled>
          {buttonLabel}
        </Button>
      </>
    );
  }

  const buttonLabel =
    carePlans.renewal_suggestion === 'expiring'
      ? t('diabetesForms.renew') // For now, expiring also displays 'renew' but in the future more cases will come
      : carePlans.renewal_suggestion === 'add_insulin'
        ? t('diabetesForms.addInsulin')
        : t('diabetesForms.renew');

  return (
    <>
      <Button
        id={`renew-tls-button-${dataSource}`}
        data-source={dataSource}
        data-action="renew-tls"
        variant="contained"
        onClick={() => setIsTelemonitoringDrawerOpen(true)}
      >
        {buttonLabel}
      </Button>
      {isTelemonitoringDrawerOpen && (
        <PatientRenewal
          patientId={patientId}
          open={isTelemonitoringDrawerOpen}
          onClose={() => setIsTelemonitoringDrawerOpen(false)}
          onSubmit={() => setIsTelemonitoringDrawerOpen(false)}
        />
      )}
    </>
  );
};
